import { FC, useEffect, useMemo, useRef, useState } from "react";
import { DefaultGridFilter } from "../../../system/hooks/useGridFilter";
import styles from "../DashboardBusinessControls/Styles/DashboardBusinessControls.module.scss"
import { useTranslation } from "react-i18next";
import { AddCart, AutoDelete, DataBaseUpload, PlayGreen, PlayRed, RemoveCart, PlayGrey, LFLReportIcon, WithoutSalesReportIcon, MandatoryAssortmentReportIcon, FalsificationReportIcon } from "../../../libs/corporate/icons/outlined/other/DashboardCollection";
import { IDashboardBusinessControlsViewDTO } from "../../../libs/coreapi-dto/accounting/dashboardBusinessControl"
import { useAppContext } from "../../../system/providers/appContextProvider";
import { DashboardBusinessControlsDataProvider } from "../../../Services/DataProviders/DashboardBusinessControlsDataProvider"
import { LoadingStatus, SysOptionName } from "../../../@types/enumsGlobal";
import { trend, typeReport, IDataFilter, IJournalFilters } from "./interfaces";
import { SysOptionsDataProvider } from "../../../Services/DataProviders/SysOptionsDataProvider";
import { ISysOptionsDTO } from "../../../libs/coreapi-dto/service/sysOptions";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import { useLocalStorageChange } from "../../../system/hooks/useLocalStorageChange";
import UserCardFilter from "./UserCardFilter";
import { Pagination } from 'antd';
import 'antd/dist/antd.min.css';
import { Spinner } from "../../../components/spiner/Spinner";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { ContractorDataProvider } from "../../../Services/DataProviders/ContractorDataProvider";
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from "luxon";

const REPORT_TYPE_ORDER = [
    typeReport.Illiquid,
    typeReport.Expired,
    typeReport.ExcessStock,
    typeReport.ActualPrice,
    typeReport.SalesReport,
    typeReport.WithoutSalesReport,
    typeReport.MandatoryAssortmentReport,
    typeReport.FalsificationReport
];

const getIconByReportType = (reportType: keyof typeof typeReport) => {
    switch (reportType) {
        case 'Illiquid':
            return <RemoveCart />;
        case 'Expired':
            return<AutoDelete />;
        case 'ExcessStock':
            return <AddCart />; 
        case 'ActualPrice':
            return <DataBaseUpload />;
        case 'SalesReport':
            return <LFLReportIcon />;
        case 'WithoutSalesReport':
            return <WithoutSalesReportIcon />;
        case 'MandatoryAssortmentReport':
            return <MandatoryAssortmentReportIcon />;
        case 'FalsificationReport':
            return <FalsificationReportIcon />;
        default:
            return null;
    }
};

const getTrendComponent = (trendValue: keyof typeof trend) => {
    switch (trendValue) {
        case 'Positively':
            return <PlayGreen />;
        case 'Negative':
            return <PlayRed />;
        case 'Neutral':
            return <PlayGrey />;
        default:
            return null;
    }
};

const QUANTITY_CONTRACTORS = 9999

export const UserCard: FC = () => {
    const appContext = useAppContext()
    const tabsContext = useTabsContext()
    const userContext = useUserContext();
    
    const dashboardBusinessControlsDP = new DashboardBusinessControlsDataProvider(appContext.coreApiService)
    const sysOptionsDP = new SysOptionsDataProvider(appContext.coreApiService)
    const contractorDataProvider = new ContractorDataProvider(appContext.coreApiService)

    const { t } = useTranslation();

    const [allData, setAllData] = useState<IDashboardBusinessControlsViewDTO[]>([]);
    const [filteredData, setFilteredData] = useState<IDashboardBusinessControlsViewDTO[]>([]);
    const [dataFilter, setDataFilter] = useState<IDataFilter>();
    const [filteredContractors, setFilteredContractors] = useState<string[]>([]);

    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.InProcess);

    const [_, setFilters] = useLocalStorageChange<IJournalFilters>('journalFilters');    

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const roles = userContext.userRoles.flat?.() ?? userContext.userRoles;
    const isSuperUser = roles.includes('SuperUser');
    const isAdministrator = roles.includes('Administrator');

    const [modifiedFilter, setModifiedFilter] = useState<IGridFilter>({
        ...DefaultGridFilter,
        pageNumber: 1,
        numberPerPage: QUANTITY_CONTRACTORS,       
    });

    const fetchDataRef = useRef<(filter: IGridFilter) => void>();

     const formatNumber = (value: number | null | undefined): string => {
        if (value == null || isNaN(value)) return '-';
        return value.toLocaleString('ru-RU');
    };

    useEffect(() => {
        const updateFilter = () => {
            if (isAdministrator || isSuperUser) {
                const newFilter = {
                    ...modifiedFilter,                   
                    columnFilters: []
                };
                setModifiedFilter(newFilter);
                fetchDataRef.current?.(newFilter);
            } else {
                contractorDataProvider.getMe((entity) => {
                    const newFilter = {
                        ...modifiedFilter,                        
                        columnFilters: [{
                            name: 'idContractorGlobal',
                            operator: 'Eq',
                            value: entity.idGlobal
                        }],
                    } as IGridFilter;
                    setModifiedFilter(newFilter);
                    fetchDataRef.current?.(newFilter);
                },);
            }
        };
        updateFilter();       
    }, [isAdministrator, isSuperUser]);
    
    useEffect(() => {
        sysOptionsDP.getByName(SysOptionName.DashboardFilterSettings, (e) => {
            const data = e as ISysOptionsDTO;
            try {
                const jsonData = JSON.parse(data.jsonData);

                const settings = jsonData.Value;

                setDataFilter(settings);
            } catch (error) {
                console.error("Ошибка при парсинге jsonData:", error);
            }
        });
    }, []);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        
        fetchDataRef.current = (filter: IGridFilter) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setLoadingStatus(LoadingStatus.InProcess);
                dashboardBusinessControlsDP.getView(
                    modifiedFilter,
                    (data) => {
                        setAllData(data);                        
                        setLoadingStatus(LoadingStatus.Completed);
                    },
                    (error) => {
                        console.error('Ошибка загрузки:', error);
                        setLoadingStatus(LoadingStatus.Failed);
                    }
                );
            }, 300);
        };
        fetchDataRef.current?.(modifiedFilter);
    
        return () => clearTimeout(timeoutId);
    }, [modifiedFilter]);

    useEffect(() => {        
        const filtered = filteredContractors.length > 0
            ? allData.filter(entity => filteredContractors.includes(entity.idContractorGlobal))
            : allData;
        
        setFilteredData(filtered);
       
    }, [allData, filteredContractors, currentPage, pageSize]);

    useEffect(() => {
        setLoadingStatus(allData?.length > 0 ? LoadingStatus.Completed : LoadingStatus.InProcess);
    }, [allData]);
        
    const handleOpenJournal = (reportType: keyof typeof typeReport, idGlobal: string, contractorId: string, contractorName: string) => {
        const tabId = uuidv4();
        const journalFilters = {
            tabId,
            contractorId,
            contractorName,
            reportType: typeReport[reportType],
            filters: {
                ...(reportType === 'Illiquid' && {
                    LliquidLots :{
                        LifeLot: dataFilter?.LliquidLots?.LifeLot || 50,
                        Sales: dataFilter?.LliquidLots?.Sales || 0,
                        StartDate: dataFilter?.LliquidLots?.StartDate || DateTime.local(1900, 1, 1),
                        EndDate: dataFilter?.LliquidLots?.EndDate || DateTime.now().plus({ days: -180 }),
                    }
                    
                }),
                ...(reportType === 'ExcessStock' && {
                    ExcessStock: {
                        IsExcessStock: dataFilter?.ExcessStock?.IsExcessStock ?? true,
                        StartDate: dataFilter?.ExcessStock?.StartDate || DateTime.local(1900, 1, 1),
                        EndDate: dataFilter?.ExcessStock?.EndDate || DateTime.now(),
                    }
                    
                }),
                ...(reportType === 'Expired' && { 
                    Expired: {
                        PercentBestBefore: dataFilter?.Expired?.PercentBestBefore || 90,
                        BestBeforeMonth: dataFilter?.Expired?.BestBeforeMonth || 2,
                        CompareBestBeforeMonth: dataFilter?.Expired?.CompareBestBeforeMonth,
                        StartDate: dataFilter?.Expired?.StartDate || DateTime.now().plus({ days: -90 }),
                        EndDate: dataFilter?.Expired?.EndDate || DateTime.now(),
                    }
                   
                }),
                ...(reportType === 'ActualPrice' && {
                    DifferencePercent: dataFilter?.ActualPrice?.DifferencePercent || 5
                }),
                ...(reportType === 'SalesReport' && {
                    DashboardSalesReport :{
                        PeriodInDays: dataFilter?.DashboardSalesReport?.PeriodInDays || 30,
                        LflPercentage: dataFilter?.DashboardSalesReport?.LflPercentage || 20,
                        IncludeCheques: dataFilter?.DashboardSalesReport?.IncludeCheques || true,
                        IncludeMovements: dataFilter?.DashboardSalesReport?.IncludeMovements || false,
                        IncludeInvoiceOut: dataFilter?.DashboardSalesReport?.IncludeInvoiceOut || false
                    }
                    
                }),
                ...(reportType === 'WithoutSalesReport' && {
                    DashboardWithoutSalesReport :{
                        PeriodInDays: dataFilter?.DashboardWithoutSalesReport?.PeriodInDays || 30,
                    }
                    
                }),
                ...(reportType === 'MandatoryAssortmentReport' && {
                    DashboardMandatoryAssortment :{
                        IncludeReserve: dataFilter?.DashboardMandatoryAssortment?.IncludeReserve || true,
                        Threshold: dataFilter?.DashboardMandatoryAssortment?.Threshold || 0,
                    }
                }),
                IsGnvls: dataFilter?.IsGnvls
            } as IDataFilter
        };

        switch (reportType) {
            case 'ActualPrice':               
                tabsContext.openChild(
                    'actual_prices_lot_plugin',
                    'view',
                    idGlobal,
                    () => {},                    
                    {
                        ...journalFilters
                    }
                );
                break;
            case 'SalesReport':
                tabsContext.openChild(
                    'analysis_sales_lot_plugin',
                    'view',
                    idGlobal,
                    () => {},                    
                    {
                        ...journalFilters
                    }
                );
                break;
            case 'WithoutSalesReport':
                tabsContext.openChild(
                    'analysis_withOut_sales_lot_plugin',
                    'view',
                    idGlobal,
                    () => {},                    
                    {
                        ...journalFilters
                    }
                );
                break;
            case 'MandatoryAssortmentReport':
                tabsContext.openChild(
                    'analysis_mandatory_assortment_report_lot_plugin',
                    'view',
                    idGlobal,
                    () => {},                    
                    {
                        ...journalFilters
                    }
                );
                break;
            case 'FalsificationReport':
                tabsContext.openChild(
                    'analysis_falsification_plugin',
                    'view',
                    idGlobal,
                    () => {},                    
                    {
                        ...journalFilters
                    }
                );
                break;
            case 'Illiquid':
            case 'ExcessStock':
            case 'Expired':
            default:                
                tabsContext.openChild(
                    'analysis_lot_plugin',
                    'view',
                    idGlobal,
                    () => {},
                    {                      
                        ...journalFilters,                        
                    }
                );
                break;
        }
        setFilters(journalFilters);
    };

    const handleFilterChange = (selectedContractors: string[]) => {
        setFilteredContractors(selectedContractors);
        setCurrentPage(1);
    };

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredData.slice(startIndex, startIndex + pageSize);
    }, [filteredData, currentPage, pageSize]);

    return (
        <>
            <UserCardFilter onFilterChange={handleFilterChange} currentFilter={modifiedFilter} data={allData} />
            {loadingStatus === LoadingStatus.InProcess ? (
                <div className={styles.spinnerContainer}>
                    <Spinner />
                </div>
            ) : (
                <>
                    <div style={{ minHeight: 'calc(100vh - 200px)' }}>
                        {paginatedData
                            .map((entity) => (
                                <div key={entity.idContractorGlobal} className={styles.userCard}>
                                    <p className={styles.userCardTitle}>{entity.contractorName}</p>
                                    <div className={styles.cardItemWrapper}>

                                        {REPORT_TYPE_ORDER.map(reportType => {
                                            const item = entity.items.find(item => item.reportType === reportType);
                                            const itemIndex = entity.items.findIndex(item => item.reportType === reportType);
                                            if (!item) return null;

                                            return (
                                                <div key={item.idGlobal}>
                                                    <div className={styles.userCardItem}>
                                                    <div
                                                        className={styles.wrapperGrey}
                                                        onClick={() =>
                                                        handleOpenJournal(
                                                            item.reportType,
                                                            entity?.items[itemIndex].idGlobal,
                                                            entity.idContractorGlobal,
                                                            entity.contractorName
                                                        )
                                                        }
                                                    >
                                                        <div className={styles.wrapperLeft}>
                                                        <div className={styles.userCardItemIcon}>
                                                            {getIconByReportType(item.reportType)}
                                                        </div>

                                                        <div className={styles.contentWrapper}>
                                                            <div className={styles.userCardItemDescription}>
                                                            <div className={styles.userCardItemDescription_nameGrid}>
                                                                {item.reportTypeRus}
                                                            </div>
                                                            {item.reportType === 'Illiquid' && (
                                                                <div className={styles.userCardItemDescription_actionUser}>
                                                                {t('accounting.dashboardBusinessControls.descriptionPosWithoutSales')}
                                                                </div>
                                                            )}
                                                            </div>
                                                            <div className={styles.userCardItemSums}>
                                                            <div className={styles.sumRow}>
                                                                <span className={styles.sumLabel}>
                                                                    {t('accounting.dashboardBusinessControls.supplierSumIncVat')}
                                                                </span>
                                                                <span className={styles.sumValue}>
                                                                {formatNumber(item.supplierSumIncVat)} ₽
                                                                </span>
                                                            </div>
                                                            <div className={styles.sumRow}>
                                                                <span className={styles.sumLabel}>
                                                                    {t('accounting.dashboardBusinessControls.retailSumIncVat')}
                                                                </span>
                                                                <span className={styles.sumValue}>
                                                                {formatNumber(item.retailSumIncVat)} ₽
                                                                </span>
                                                            </div>
                                                            </div>
                                                        </div>
                                                        </div>

                                                        <div className={styles.wrapperRight}>
                                                        <div className={styles.userCardItemViolations}>
                                                            {item.quantityFailLots}
                                                        </div>
                                                        </div>
                                                    </div>

                                                    <div className={styles.userCardItemTrend}>
                                                        {getTrendComponent(item.trend)}
                                                    </div>
                                                    </div>
                                                </div>
                                                );
                                        })}
                                    </div>
                                </div>
                            ))
                        }
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '20px 0',
                        marginTop: '20px',
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={filteredData.length}
                            onChange={(page, pageSize) => {
                                setCurrentPage(page);
                                if (pageSize) setPageSize(pageSize);
                                window.scrollTo(0, 0);
                            }}
                            showSizeChanger
                            pageSizeOptions={['10', '20', '50', '100']}
                            showTotal={(total, range) =>
                                `${range[0]}-${range[1]} из ${total} аптек`
                            }
                            style={{ margin: '0 auto' }}
                        />
                    </div>
                </>)}
        </>
    );
}